import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import BaseModal from '../../components/common/BaseModal/BaseModal';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { DropdownIndicator } from '../../components/common/SelectComponents';
import { formatOptionLabel, getSelectStyles } from '../../utils/selectStyles';
import { connectionService, repositoryService } from '../../services/apiService';
import API_ENDPOINTS from '../../config/apiConfig';
import { prepareRepositoryPat } from '../../utils/encrypt';
import './AddRepositoryModal.css';

const initialFormData = {
    connectionName: '',
    pat: '',
    username: '',
    repository: '',
    branch: '',
    comments: '',
};

const CHARACTER_LIMITS = {
    connectionName: 100,
    username: 40,
    comments: 50,
};

const EditRepositoryModal = ({ isOpen, onClose, onSuccess, repoData, setIsLoading }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});

    // Mirrors EditConnectionModal's passwordFieldsTouched pattern exactly
    const [patTouched, setPatTouched] = useState(false);
    const [showPat, setShowPat] = useState(false);

    const [serverPublicKey, setServerPublicKey] = useState(null);

    const [isTesting, setIsTesting] = useState(false);
    const [isTestSuccessful, setIsTestSuccessful] = useState(false);

    const [repositories, setRepositories] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [loadingRepositories, setLoadingRepositories] = useState(false);

    const modalBodyRef = useRef(null);

    // ─── Derived ───────────────────────────────────────────────────────────────

    // Exact condition from EditConnectionModal for showing the eye icon:
    // (touched || value==="") && (value !== "•••••" || touched)
    const showEyeButton =
        (patTouched || formData.pat === '') &&
        (formData.pat !== '•••••' || patTouched);

    // Test button is disabled while PAT still shows the untouched placeholder
    const isTestDisabled = !patTouched && formData.pat === '•••••';

    // Update requires repo + branch selected.
    // If PAT was touched (user changed it), a successful test is also required.
    const canUpdate =
        (!patTouched || isTestSuccessful) &&
        formData.repository &&
        formData.branch;

    // ─── Fetch public key ──────────────────────────────────────────────────────

    useEffect(() => {
        const fetchPublicKey = async () => {
            try {
                const publicKey = await connectionService.getPublicKey();
                setServerPublicKey(publicKey);
            } catch (err) {
                console.error("Failed to fetch server public key:", err);
            }
        };

        fetchPublicKey();
    }, []);

    // ─── Prefill form when modal opens ────────────────────────────────────────

    useEffect(() => {
        if (isOpen && repoData) {
            setFormData({
                connectionName: repoData.name || '',
                pat: '•••••',
                username: repoData.github_username || '',
                repository: repoData.repository_full_name || repoData.repository || '',
                branch: repoData.branch || '',
                comments: repoData.comment || '',
            });

            setPatTouched(false);
            setShowPat(false);
            setIsTestSuccessful(false);
            setErrors({});
            setRepositories([]);
            setBranches(repoData.branch ? [{ value: repoData.branch, label: repoData.branch }] : []);

            // Fetch live repository list using the connection id
            const fetchRepositories = async () => {
                setIsLoading(true);
                setLoadingRepositories(true);
                try {
                    const result = await repositoryService.getRepoValues({
                        github_connection_id: repoData.id,
                        github_username: repoData.github_username,
                    });
                    const raw = Array.isArray(result) ? result : (result?.repositories ?? []);
                    const repoOptions = raw.map(r => ({ value: r.full_name, label: r.full_name }));
                    setRepositories(repoOptions);
                    // Stored repository may no longer be valid (e.g. lost access) — drop it if it's not in the live list
                    setFormData(prev => {
                        if (prev.repository && !repoOptions.some(o => o.value === prev.repository)) {
                            return { ...prev, repository: '', branch: '' };
                        }
                        return prev;
                    });
                } catch {
                    toast.error('Failed to load repositories');
                    setRepositories([]);
                    // Can't confirm the stored repository is still valid (e.g. expired PAT) — clear it so it can't be silently resubmitted
                    setFormData(prev => ({ ...prev, repository: '', branch: '' }));
                } finally {
                    setLoadingRepositories(false);
                    setIsLoading(false);
                }
            };

            const fetchBranches = async () => {
                setIsLoading(true);
                // setLoadingRepositories(true);
                try {
                    const result = await repositoryService.getBranchValues({
                        github_connection_id: repoData.id,
                        // github_username: repoData.github_username,
                        repository: repoData.repository_full_name,
                    });
                    const raw = Array.isArray(result) ? result : (result?.branches ?? []);
                    const branchOptions = raw.map(b => ({ value: b.name, label: b.name }));
                    setBranches(branchOptions);
                    // Stored branch may no longer be valid — drop it if it's not in the live list
                    setFormData(prev => {
                        if (prev.branch && !branchOptions.some(o => o.value === prev.branch)) {
                            return { ...prev, branch: '' };
                        }
                        return prev;
                    });
                } catch {
                    toast.error('Failed to load branches');
                    setBranches([]);
                    // Can't confirm the stored branch is still valid (e.g. expired PAT) — clear it so it can't be silently resubmitted
                    setFormData(prev => ({ ...prev, branch: '' }));
                } finally {
                    // setLoadingRepositories(false);
                    setIsLoading(false);
                }
            };
            fetchRepositories();
            fetchBranches();
        }
    }, [isOpen, repoData]);

    // ─── PAT handlers — mirrors EditConnectionModal password field exactly ─────

    const handlePatFocus = () => {
        if (formData.pat === '•••••') {
            setFormData(prev => ({ ...prev, pat: '' }));
            setPatTouched(true);
        }
    };

    const handlePatBlur = () => {
        // Restore placeholder if user left the field empty
        if (formData.pat === '') {
            setFormData(prev => ({ ...prev, pat: '•••••' }));
            setPatTouched(false);
            setShowPat(false);
        }
    };

    const handlePatChange = (value) => {
        setPatTouched(true);
        setIsTestSuccessful(false);

        // Always clear repo/branch as soon as user types a new PAT value
        setFormData(prev => ({ ...prev, pat: value, repository: '', branch: '' }));
        setRepositories([]);
        setBranches([]);

        if (errors.pat) setErrors(prev => { const n = { ...prev }; delete n.pat; return n; });
    };

    // ─── Other field handlers ──────────────────────────────────────────────────

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

        // Username drives the repo list — changing it invalidates existing repos/branches
        if (field === 'username') {
            setIsTestSuccessful(false);
            setRepositories([]);
            setBranches([]);
            setFormData(prev => ({ ...prev, username: value, repository: '', branch: '' }));
        }
    };

    const handleRepositoryChange = async (opt) => {
        const value = opt ? opt.value : '';
        setFormData(prev => ({ ...prev, repository: value, branch: '' }));
        setBranches([]);
        if (errors.repository) setErrors(prev => { const n = { ...prev }; delete n.repository; return n; });

        if (value) {
            setIsLoading(true);
            try {
                const branchPayload = patTouched
                    ? { repository: value, ...await prepareRepositoryPat(formData.pat, serverPublicKey) }
                    : { repository: value, github_connection_id: repoData.id };

                const result = await repositoryService.getBranchValues(branchPayload);
                // const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
                // const result = await repositoryService.getBranchValues({
                //     repository: value,
                //     github_connection_id: repoData.id,
                //     ...encryptedPat,
                // });
                const raw = Array.isArray(result) ? result : (result?.branches ?? []);
                setBranches(raw.map(b => ({ value: b.name, label: b.name })));
            } catch {
                toast.error('Failed to load branches');
                setBranches([]);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleBranchChange = (opt) => {
        setFormData(prev => ({ ...prev, branch: opt ? opt.value : '' }));
        if (errors.branch) setErrors(prev => { const n = { ...prev }; delete n.branch; return n; });
    };

    // ─── Validation ────────────────────────────────────────────────────────────

    const validatePreTest = () => {
        const errs = {};
        if (!formData.pat.trim()) errs.pat = 'Please enter a new PAT to test';
        if (!formData.username.trim()) errs.username = 'Username is required';
        else if (formData.username.length > CHARACTER_LIMITS.username)
            errs.username = `Username exceeds the ${CHARACTER_LIMITS.username} character limit`;
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateForUpdate = () => {
        const errs = {};
        if (patTouched && !formData.pat.trim()) errs.pat = 'Please enter a valid PAT';
        if (!formData.username.trim()) errs.username = 'Username is required';
        else if (formData.username.length > CHARACTER_LIMITS.username)
            errs.username = `Username exceeds the ${CHARACTER_LIMITS.username} character limit`;
        if (!formData.repository) errs.repository = 'Repository is required';
        if (!formData.branch) errs.branch = 'Branch is required';
        if (formData.comments.length > CHARACTER_LIMITS.comments)
            errs.comments = `Comments exceed the ${CHARACTER_LIMITS.comments} character limit`;
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ─── Test Connection ───────────────────────────────────────────────────────

    const handleTestConnection = async () => {
        if (!validatePreTest()) return;
        if (!serverPublicKey) {
            toast.error('Encryption key not loaded yet. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
            const result = await repositoryService.getRepoValues({
                github_username: formData.username,
                ...encryptedPat,
            });

            const raw = Array.isArray(result) ? result : (result?.repositories ?? []);
            const repoOptions = raw.map(r => ({
                value: r.full_name,
                label: r.full_name,
            }));

            setRepositories(repoOptions);
            setIsTestSuccessful(true);

            // Clear selections so user picks fresh from live list
            setFormData(prev => ({ ...prev, repository: '', branch: '' }));
            setBranches([]);

            toast.success('Connection tested successfully');
        } catch (error) {
            setIsTestSuccessful(false);
            toast.error(error.message || 'Test connection failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Update Repository ─────────────────────────────────────────────────────

    const handleUpdate = async () => {
        if (!validateForUpdate()) return;
        if (!serverPublicKey) {
            toast.error('Encryption key not loaded yet. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
            const payload = {
                github_username: formData.username,
                repository: formData.repository,
                repository_full_name: formData.repository,
                branch: formData.branch,
                comment: formData.comments || null,
                ...encryptedPat,
            };

            await repositoryService.updateRepo(repoData.id, payload);
            toast.success('Repository updated successfully');
            await onSuccess?.();
            handleClose();
        } catch (error) {
            toast.error(error.message || 'Failed to update repository. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Close / reset ─────────────────────────────────────────────────────────

    const handleClose = () => {
        setFormData(initialFormData);
        setErrors({});
        setPatTouched(false);
        setShowPat(false);
        setIsTestSuccessful(false);
        setRepositories([]);
        setBranches([]);
        onClose();
    };

    // ─── Footer ────────────────────────────────────────────────────────────────

    const footer = (
        <>
            <button
                type="button"
                className="btn btn-cancel"
                onClick={handleClose}
            // disabled={isTesting}
            >
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-secondary"
                onClick={handleTestConnection}
                disabled={isTestDisabled}
            >
                Test Connection
            </button>
            <button
                type="button"
                className={"btn btn-primary"}
                // className={`btn btn-primary${!canUpdate ? ' btn-primary--disabled' : ''}`}
                onClick={handleUpdate}
            // disabled={!canUpdate}
            >
                Update Connection
            </button>
        </>
    );

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Edit Repository"
            footer={footer}
            className="add-repository-modal"
            modalBodyRef={modalBodyRef}
        >
            <div className="add-repository-form">

                {/* Connection Name — always disabled */}
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>
                            Connection Name
                        </label>
                        <input
                            type="text"
                            value={formData.connectionName}
                            disabled
                        />
                    </div>
                </div>

                {/* PAT + Username */}
                <div className="form-row">
                    <div className="form-group">
                        <label>
                            PAT <span className="required">*</span>
                        </label>
                        {/* Eye icon logic mirrors EditConnectionModal's Enter Password exactly */}
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPat ? 'text' : 'password'}
                                placeholder="Enter Personal Access Token"
                                autoComplete="new-password"
                                value={formData.pat}
                                onChange={(e) => handlePatChange(e.target.value)}
                                onFocus={handlePatFocus}
                                onBlur={handlePatBlur}
                                className={errors.pat ? 'error' : ''}
                                style={{
                                    paddingRight: showEyeButton ? '2.083vw' : '0.729vw',
                                }}
                            />
                            {showEyeButton && (
                                <button
                                    type="button"
                                    onClick={() => setShowPat(v => !v)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.521vw',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '0.208vw',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#64748b',
                                    }}
                                    title={showPat ? 'Hide PAT' : 'Show PAT'}
                                    tabIndex={-1}
                                >
                                    {showPat
                                        ? <Eye style={{ width: '0.938vw', height: '0.938vw' }} />
                                        : <EyeOff style={{ width: '0.938vw', height: '0.938vw' }} />
                                    }
                                </button>
                            )}
                        </div>
                        <ErrorMessage message={errors.pat} />
                    </div>

                    <div className="form-group">
                        <label>
                            Username <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., john.doe"
                            autoComplete="off"
                            value={formData.username}
                            onChange={(e) => handleChange('username', e.target.value)}
                            className={errors.username ? 'error' : ''}
                        />
                        <ErrorMessage message={errors.username} />
                    </div>
                </div>

                {/* Repository + Branch + Comments — always visible, dropdowns locked until test passes */}
                <div className="form-row">
                    <div className="form-group">
                        <label>
                            Repository <span className="required">*</span>
                        </label>
                        <Select
                            value={repositories.find(o => o.value === formData.repository) || null}
                            onChange={handleRepositoryChange}
                            options={repositories}
                            placeholder="Select Repository"
                            isSearchable
                            isClearable
                            formatOptionLabel={formatOptionLabel}
                            isDisabled={loadingRepositories}
                            styles={getSelectStyles(!!errors.repository)}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                            noOptionsMessage={() => 'No repositories found'}
                            components={{ DropdownIndicator }}
                        />
                        <ErrorMessage message={errors.repository} />
                    </div>

                    <div className="form-group">
                        <label>
                            Branch <span className="required">*</span>
                        </label>
                        <Select
                            value={branches.find(o => o.value === formData.branch) || null}
                            onChange={handleBranchChange}
                            options={branches}
                            placeholder="Select Branch"
                            isSearchable
                            isClearable
                            formatOptionLabel={formatOptionLabel}
                            // isDisabled={!formData.repository}
                            styles={getSelectStyles(!!errors.branch)}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                            noOptionsMessage={() => 'No branches found'}
                            components={{ DropdownIndicator }}
                        />
                        <ErrorMessage message={errors.branch} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>Comments</label>
                        <input
                            type='text'
                            placeholder="Your comments here"
                            value={formData.comments}
                            onChange={(e) => handleChange('comments', e.target.value)}
                            className={errors.comments ? 'error' : ''}
                        />
                        <ErrorMessage message={errors.comments} />
                    </div>
                </div>

            </div>
        </BaseModal>
    );
};

export default EditRepositoryModal;